import { Answer } from "@/interfaces/Participants";
import { GridActionsCellItem, GridRowParams } from "@mui/x-data-grid-pro";
import { SyntheticEvent } from "react";
import { Utils } from "../../common/Utils";
import MoreVertIcon from "../../resources/images/icons-more-vertical.svg";
import DeleteIcon from "../../resources/images/icons-remove.svg";
import { KLAYO_COLORS } from "../../themes";
import type { BaseTableRow, TableColumnDef, TableProps } from "../../types/components/table.types";
import { getHeaderName } from "../../types/components/table.types";
import { highlightKeyword } from "../../utilities";
import { Chip } from "../Chip";
import { Table } from "./Table";

interface CheckListsTableRow extends BaseTableRow {
  name?: string | null;
  answers?: Answer[];
  lastUpdatedDate?: string;
  sectionCount?: number;
  questionCount?: number;
  deleted?: boolean;
}

/** Props specific to CheckListsTable */
interface CheckListsTableProps<TData extends CheckListsTableRow> extends Omit<
  TableProps<TData>,
  "onRowClick"
> {
  onChecklistAction?: (event: SyntheticEvent, row: TData) => void;
  onDeleteCheckListAction?: (event: SyntheticEvent, row: TData) => void;
  keyword?: string;
  showProficiency?: boolean;
  rowHasAction?: boolean | null;
  onChecklistClick?: (row: TData) => void;
}

export class CheckListsTable<T extends CheckListsTableRow> extends Table<
  T,
  CheckListsTableProps<T>
> {
  static columns = {
    lastUpdated: {
      field: "lastUpdatedDate",
      label: "tableColumns.lastUpdated"
    },
    checklistName: {
      field: "name",
      label: "tableColumns.checklistName"
    },
    sections: {
      field: "sectionCount",
      label: "tableColumns.sections"
    },
    questions: {
      field: "questionCount",
      label: "tableColumns.questions"
    },
    actions: {
      field: "actions",
      label: "tableColumns.actions"
    },
    status: {
      field: "status",
      label: "tableColumns.status"
    },
    delete: {
      field: "delete",
      label: "tableColumns.delete"
    },
    icon: {
      field: "icon",
      label: "tableColumns.icon"
    }
  };

  static defaultProps = {
    ...Table.defaultProps,
    columns: [
      { type: CheckListsTable.columns.lastUpdated },
      { type: CheckListsTable.columns.checklistName },
      { type: CheckListsTable.columns.sections },
      { type: CheckListsTable.columns.questions },
      { type: CheckListsTable.columns.actions }
    ],
    noRowsMessage: "No rows, yet",
    showProficiency: false,
    rowHasAction: null
  };

  constructor(props: CheckListsTableProps<T>) {
    super(props);

    this.state = {
      ...this.state,
      sortModel: this.props.sortModel
        ? this.props.sortModel
        : [
            {
              field: "lastUpdatedDate",
              sort: "asc"
            }
          ]
    };
  }

  componentDidUpdate(prevProps: CheckListsTableProps<T>): void {
    if (prevProps.keyword !== this.props.keyword) {
      this.initColumns();
    }
    // Update sortModel when props.sortModel changes to sync header arrows
    if (prevProps.sortModel !== this.props.sortModel) {
      this.setState({ sortModel: this.props.sortModel });
    }
  }

  initColumns(): void {
    const { columns, sortable, onChecklistAction, keyword } = this.props;
    this.columns = [];

    columns?.forEach((c: TableColumnDef<T>) => {
      if (this.hasColumn(c)) {
        if (c.type === CheckListsTable.columns.lastUpdated) {
          this.columns.push({
            field: "lastUpdatedDate",
            headerName: getHeaderName(c, this.props.translate),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 200,
            headerAlign: "left",
            align: "left",
            valueGetter: (_value, row) => row.lastUpdatedDate,
            renderCell: (params) => Utils.formatDate(params.row.lastUpdatedDate || "")
          });
        }

        if (c.type === CheckListsTable.columns.checklistName) {
          this.columns.push({
            field: "name",
            headerName: getHeaderName(c, this.props.translate),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            flex: 1,
            valueGetter: (_value, row) => row.name,
            renderCell: c.renderCell
              ? c.renderCell
              : (params) => highlightKeyword(params.row.name || "", keyword)
          });
        }

        if (c.type === CheckListsTable.columns.sections) {
          this.columns.push({
            field: "sectionCount",
            headerName: getHeaderName(c, this.props.translate),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 120,
            headerAlign: "center",
            align: "center",
            valueGetter: (_value, row) => row.sectionCount,
            cellClassName: "Klayo_associated-attribute-cell",
            renderCell: (params) => params.row.sectionCount
          });
        }

        if (c.type === CheckListsTable.columns.questions) {
          this.columns.push({
            field: "questionCount",
            headerName: getHeaderName(c, this.props.translate),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            width: 120,
            headerAlign: "center",
            align: "center",
            valueGetter: (_value, row) => row.questionCount,
            cellClassName: "Klayo_associated-attribute-cell",
            renderCell: (params) => params.row.questionCount
          });
        }

        if (c.type === CheckListsTable.columns.actions) {
          this.columns.push({
            field: "actions",
            type: "actions",
            filterable: false,
            disableReorder: true,
            align: "right",
            width: 50,
            valueGetter: (_value, row) => (row.deleted ? null : row),
            getActions: (params) => [
              <GridActionsCellItem
                icon={<MoreVertIcon />}
                label='Actions'
                onClick={(e: any) => onChecklistAction?.(e, params.row)}
                key={Math.random().toString(36).substring(7)}
              />
            ]
          });
        }

        if (c.type === CheckListsTable.columns.status) {
          const { translate } = this.props;
          this.columns.push({
            width: 150,
            field: "status",
            filterable: false,
            disableReorder: true,
            align: "right",
            valueGetter: (_value, row) => (row.deleted ? null : row),
            renderCell: (params) => {
              const isComplete = Utils.checkAllAnswersComplete(
                params?.row?.answers || Utils.getAnswersFromChecklist(params?.row || {})
              );

              return (
                <Chip
                  label={
                    isComplete
                      ? translate?.("status.completed") || "Completed"
                      : translate?.("status.incomplete") || "Incomplete"
                  }
                  size='small'
                  variant='outlined'
                  textColor={isComplete ? KLAYO_COLORS.LimeGreenLG600 : KLAYO_COLORS.Red600}
                  backgroundColor={isComplete ? KLAYO_COLORS.LimeGreenLG050 : KLAYO_COLORS.Rose050}
                  key={Math.random().toString(36).substring(7)}
                />
              );
            }
          });
        }

        if (c.type === CheckListsTable.columns.delete) {
          this.columns.push({
            width: 50,
            field: "delete",
            type: "actions",
            filterable: false,
            disableReorder: true,
            align: "right",
            valueGetter: (_value, row) => (row.deleted ? null : row),
            getActions: (params) => [
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label='Actions'
                onClick={(e) => this.onDeleteCheckListAction(e, params.row)}
                key={Math.random().toString(36).substring(7)}
              />
            ]
          });
        }

        if (c.type === CheckListsTable.columns.icon)
          this.columns.push({
            field: "icon",
            type: "icon",
            filterable: false,
            disableReorder: true,
            align: "center",
            renderCell: c.renderCell,
            width: 50
          });
      }
    });
  }

  onDeleteCheckListAction = (e: SyntheticEvent, row: T): void => {
    const { onDeleteCheckListAction } = this.props;
    if (onDeleteCheckListAction) onDeleteCheckListAction(e, row);
  };

  onRowClick = (row: GridRowParams<T>): void => {
    const { onChecklistClick } = this.props;
    onChecklistClick?.(row?.row);
  };
}
